/**
 * The classification drawing as DXF, for the client's CAD team.
 *
 * The model is written at full size: one drawing unit is one metre, in the project's own
 * coordinates, so a plot plan and these zones line up without anyone scaling anything. Around it
 * sits a real A3 border and title block, drawn at the size they need to be to plot at the chosen
 * scale — which is how a draughtsman works in model space, and is the thing the competitor leaves
 * as a placeholder.
 *
 * Zones go on their own layers by zone type, with their hatch on a parallel layer, so a CAD user can
 * turn the fill off and keep the boundary.
 */
import { boxOutline, chooseScale, forArea, project, zoneOutline, type DrawingInput, type Outline } from '../drawing/drawing';
import { hatchDots, hatchLines } from './hatch';
import { writeDxf, type Entity, type Layer, type Point } from './dxf';
import type { ZoneNumber } from '../model/types';

const SHEET = { w: 420, h: 297 };
const MARGIN = 10;
const TITLE = { w: 180, h: 46 };
const KEY_W = 62;

const L = {
  area: 'HAC-AREA',
  plant: 'HAC-PLANT',
  equipment: 'HAC-EQUIPMENT',
  equipmentFail: 'HAC-EQUIPMENT-FAIL',
  source: 'HAC-SOURCE',
  text: 'HAC-TEXT',
  sheet: 'HAC-SHEET',
  zone: (z: ZoneNumber) => `HAC-ZONE-${z}`,
  zoneHatch: (z: ZoneNumber) => `HAC-ZONE-${z}-HATCH`,
};

/** AutoCAD colour indices: red, orange and yellow for zones 0, 1 and 2. */
const ZONE_COLOUR: Record<ZoneNumber, number> = { 0: 1, 1: 30, 2: 2 };

const LAYERS: Layer[] = [
  { name: L.zone(0), colour: ZONE_COLOUR[0] },
  { name: L.zoneHatch(0), colour: ZONE_COLOUR[0] },
  { name: L.zone(1), colour: ZONE_COLOUR[1] },
  { name: L.zoneHatch(1), colour: ZONE_COLOUR[1] },
  { name: L.zone(2), colour: ZONE_COLOUR[2] },
  { name: L.zoneHatch(2), colour: ZONE_COLOUR[2] },
  { name: L.area, colour: 7 },
  { name: L.plant, colour: 8 },
  { name: L.equipment, colour: 5 },
  { name: L.equipmentFail, colour: 1 },
  { name: L.source, colour: 6 },
  { name: L.text, colour: 7 },
  { name: L.sheet, colour: 7 },
];

/** Hatch of each zone type, in millimetres on the plotted sheet (cf. the key on the SVG sheet). */
const HATCH: Record<ZoneNumber, { spacingMm: number; angles: number[]; dots?: boolean }> = {
  0: { spacingMm: 2.0, angles: [], dots: true },
  1: { spacingMm: 1.8, angles: [45, 135] },
  2: { spacingMm: 2.2, angles: [45] },
};

export function drawingDxf(raw: DrawingInput): string {
  const input = raw.areaId ? forArea(raw) : raw;
  const { doc, view } = input;
  const e: Entity[] = [];

  const zones = input.zones.map((z) => ({ z, o: zoneOutline(view, z) }));

  // 1. the model, at one unit to the metre
  for (const a of doc.areas) {
    if (!a.box) continue;
    e.push({ kind: 'polyline', layer: L.area, points: boxOutline(view, a.box), closed: true });
  }
  for (const o of doc.objects) {
    if (o.kind === 'box') { e.push({ kind: 'polyline', layer: L.plant, points: boxOutline(view, o.box), closed: true }); continue; }
    if (view === 'plan') e.push({ kind: 'circle', layer: L.plant, centre: [o.base[0], o.base[1]], radius: o.radiusM });
    else e.push({ kind: 'polyline', layer: L.plant, points: [[o.base[0] - o.radiusM, o.base[2]], [o.base[0] + o.radiusM, o.base[2]], [o.base[0] + o.radiusM, o.base[2] + o.heightM], [o.base[0] - o.radiusM, o.base[2] + o.heightM]], closed: true });
  }
  for (const { z, o } of zones) {
    const layer = L.zone(z.zone);
    if (o.kind === 'circle') e.push({ kind: 'circle', layer, centre: [o.cu, o.cv], radius: o.r });
    else e.push({ kind: 'polyline', layer, points: o.pts, closed: true });
  }
  for (const eq of doc.equipment) {
    e.push({ kind: 'polyline', layer: input.failedEquipment.has(eq.id) ? L.equipmentFail : L.equipment, points: boxOutline(view, eq.box), closed: true });
  }

  // 2. the sheet around it, sized so that it plots at a standard scale
  const bounds = extentOf(e);
  const availW = SHEET.w - 2 * MARGIN - KEY_W;
  const availH = SHEET.h - 2 * MARGIN;
  const scale = chooseScale(Math.max(bounds.maxU - bounds.minU, 1e-6), Math.max(bounds.maxV - bounds.minV, 1e-6), availW, availH);
  const k = scale / 1000; // millimetres on the sheet → metres in the model
  const centre: Point = [(bounds.minU + bounds.maxU) / 2, (bounds.minV + bounds.maxV) / 2];
  /** A point on the A3 sheet, in millimetres from its bottom-left corner, as a point in the model. */
  const sheet = (xMm: number, yMm: number): Point => [
    centre[0] + (xMm - (MARGIN + availW / 2)) * k,
    centre[1] + (yMm - (MARGIN + availH / 2)) * k,
  ];
  const mm = (v: number) => v * k;

  // 3. hatch, after the scale is known: the spacing is what it should measure on paper
  for (const { z, o } of zones) {
    const pattern = HATCH[z.zone];
    const layer = L.zoneHatch(z.zone);
    if (pattern.dots) {
      for (const c of hatchDots(o, mm(pattern.spacingMm))) e.push({ kind: 'circle', layer, centre: c, radius: mm(0.5) });
      continue;
    }
    for (const angle of pattern.angles) {
      for (const [from, to] of hatchLines(o, mm(pattern.spacingMm), angle)) e.push({ kind: 'line', layer, from, to });
    }
  }

  // 4. labels, at a size that reads on the plot
  const tagHeight = mm(2.2);
  for (const a of doc.areas) {
    if (!a.box) continue;
    const top = Math.max(...boxOutline(view, a.box).map((p) => p[1]));
    e.push({ kind: 'text', layer: L.text, at: [a.box.center[0], top + mm(1.2)], height: tagHeight, text: a.name, align: 'centre' });
  }
  for (const s of doc.sources) {
    const [u, v] = project(view, s.position);
    const r = mm(1.2);
    e.push({ kind: 'line', layer: L.source, from: [u - r, v], to: [u + r, v] });
    e.push({ kind: 'line', layer: L.source, from: [u, v - r], to: [u, v + r] });
    e.push({ kind: 'text', layer: L.text, at: [u + r * 1.4, v + r * 0.4], height: tagHeight, text: s.tag });
  }
  for (const eq of doc.equipment) {
    const [u, v] = project(view, eq.box.center);
    e.push({ kind: 'text', layer: L.text, at: [u, v - mm(2.6)], height: tagHeight, text: eq.tag, align: 'centre' });
  }

  e.push(...sheetFurniture(input, { sheet, mm, scale }));
  return writeDxf({ layers: LAYERS, entities: e });
}

interface Sheet {
  sheet: (xMm: number, yMm: number) => Point;
  mm: (v: number) => number;
  scale: number;
}

/** The border, the title block and the key: what makes it a drawing rather than a pile of geometry. */
function sheetFurniture(input: DrawingInput, { sheet, mm, scale }: Sheet): Entity[] {
  const { doc } = input;
  const e: Entity[] = [];
  const rect = (x0: number, y0: number, x1: number, y1: number, layer = L.sheet): Entity =>
    ({ kind: 'polyline', layer, points: [sheet(x0, y0), sheet(x1, y0), sheet(x1, y1), sheet(x0, y1)], closed: true });

  e.push(rect(0, 0, SHEET.w, SHEET.h));
  e.push(rect(MARGIN, MARGIN, SHEET.w - MARGIN, SHEET.h - MARGIN));

  const tx = SHEET.w - MARGIN - TITLE.w;
  const ty = MARGIN;
  e.push(rect(tx, ty, SHEET.w - MARGIN, ty + TITLE.h));
  const line = (x0: number, y0: number, x1: number, y1: number): Entity => ({ kind: 'line', layer: L.sheet, from: sheet(x0, y0), to: sheet(x1, y1) });
  e.push(line(tx, ty + 30, SHEET.w - MARGIN, ty + 30));
  e.push(line(tx, ty + 12, SHEET.w - MARGIN, ty + 12));
  e.push(line(tx + 96, ty, tx + 96, ty + 12));
  e.push(line(tx + 134, ty, tx + 134, ty + 12));

  const label = (x: number, y: number, s: string, h = 1.8): Entity => ({ kind: 'text', layer: L.text, at: sheet(x, y), height: mm(h), text: s });
  e.push(label(tx + 4, ty + 38, doc.meta.name, 3.4));
  e.push(label(tx + 4, ty + 32.5, [doc.meta.client, doc.meta.site].filter(Boolean).join(' · '), 2.2));
  e.push(label(tx + 4, ty + 24, input.view === 'plan' ? 'Hazardous area classification — plan' : 'Hazardous area classification — elevation looking north', 2.2));
  e.push(label(tx + 4, ty + 18, 'Classified to IEC 60079-10-1:2020. Zones on HAC-ZONE-* layers; one drawing unit is one metre.', 1.6));
  e.push(label(tx + 4, ty + 5, `Drawing ${input.drawingNumber}`, 2));
  e.push(label(tx + 100, ty + 5, `Rev ${input.revision}`, 2));
  e.push(label(tx + 138, ty + 5, input.date, 2));
  e.push(label(SHEET.w - MARGIN - 40, ty + 33, `Scale 1:${scale} at A3`, 2));

  // the key, up the right-hand column
  const kx = SHEET.w - MARGIN - KEY_W + 2;
  let ky = SHEET.h - MARGIN - 8;
  e.push(label(kx, ky, 'KEY', 2.4));
  ky -= 7;
  for (const z of [0, 1, 2] as ZoneNumber[]) {
    if (!input.zones.some((x) => x.zone === z)) continue;
    e.push(rect(kx, ky - 1, kx + 7, ky + 4, L.zone(z)));
    const box: Outline = { kind: 'poly', pts: [sheet(kx, ky - 1), sheet(kx + 7, ky - 1), sheet(kx + 7, ky + 4), sheet(kx, ky + 4)] };
    const pattern = HATCH[z];
    if (pattern.dots) {
      for (const c of hatchDots(box, mm(pattern.spacingMm))) e.push({ kind: 'circle', layer: L.zoneHatch(z), centre: c, radius: mm(0.5) });
    } else {
      for (const angle of pattern.angles) {
        for (const [from, to] of hatchLines(box, mm(pattern.spacingMm), angle)) e.push({ kind: 'line', layer: L.zoneHatch(z), from, to });
      }
    }
    e.push(label(kx + 9, ky, `Zone ${z}`, 2));
    ky -= 8;
  }
  if (doc.equipment.length) {
    e.push(rect(kx, ky - 1, kx + 7, ky + 4, L.equipment));
    e.push(label(kx + 9, ky, 'Equipment', 2));
    ky -= 8;
  }
  if (doc.sources.length) {
    const c = sheet(kx + 3.5, ky + 1.5);
    const r = mm(1.8);
    e.push({ kind: 'line', layer: L.source, from: [c[0] - r, c[1]], to: [c[0] + r, c[1]] });
    e.push({ kind: 'line', layer: L.source, from: [c[0], c[1] - r], to: [c[0], c[1] + r] });
    e.push(label(kx + 9, ky, 'Source of release', 2));
  }
  return e;
}

function extentOf(entities: Entity[]): { minU: number; maxU: number; minV: number; maxV: number } {
  let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
  const add = (p: Point) => { minU = Math.min(minU, p[0]); maxU = Math.max(maxU, p[0]); minV = Math.min(minV, p[1]); maxV = Math.max(maxV, p[1]); };
  for (const e of entities) {
    if (e.kind === 'line') { add(e.from); add(e.to); }
    else if (e.kind === 'polyline') e.points.forEach(add);
    else if (e.kind === 'text') add(e.at);
    else { add([e.centre[0] - e.radius, e.centre[1] - e.radius]); add([e.centre[0] + e.radius, e.centre[1] + e.radius]); }
  }
  if (!Number.isFinite(minU)) return { minU: -10, maxU: 10, minV: -10, maxV: 10 };
  return { minU, maxU, minV, maxV };
}
